import { AxiosInstance, AxiosRequestConfig } from 'axios';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useFeatureFlag } from 'configcat-react';
import { useAuth } from './useAuth';
import { useRole } from './useRole';
import { isAppInsightsStarted, trackException } from '../../telemetry/telemetry';
import { getCorrelationId } from '../../telemetry/correlationId';

export const useAxios = () => {
  const [response, setResponse] = useState<any>();
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [controller, setController] = useState<AbortController>();
  const { token } = useAuth();
  // Same `enableTelemetry` flag AppInsightsGate uses to gate the SDK itself
  // (src/telemetry/AppInsightsGate.tsx) — the correlation ID only exists to
  // stitch frontend/backend logs together for telemetry, so it's suppressed
  // on the wire whenever telemetry is off, rather than always being sent.
  // Defaults to false so telemetry stays off unless ConfigCat explicitly
  // enables it (e.g. if the flag is missing or ConfigCat is unreachable).
  // `loading` still gates this so a request fired before the flag resolves
  // never sends the header, and isAppInsightsStarted() keeps it off when the
  // flag is on but App Insights couldn't start (e.g. no connection string).
  const { value: telemetryEnabled, loading: telemetryFlagLoading } =
    useFeatureFlag('enableTelemetry', false);

  const axiosFetch = async ({
    axiosInstance,
    method,
    url,
    requestConfig = {},
  }: {
    axiosInstance: AxiosInstance;
    method: string;
    url: string;
    requestConfig: AxiosRequestConfig;
  }) => {
    // Sent as `x-correlation-id` because the backend reads that header,
    // not the App Insights SDK's own W3C `traceparent` (see design.md
    // Decision 3) — this is the ID that lets frontend/backend logs be
    // matched. Shared across the whole session (see correlationId.ts),
    // not regenerated per request.
    const correlationId = getCorrelationId();
    try {
      setLoading(true);
      const ctrl = new AbortController();
      setController(ctrl);
      const res = await axiosInstance.request({
        ...requestConfig,
        method: method.toLocaleLowerCase(),
        url,
        headers: {
          Authorization: token,
          ...(telemetryEnabled &&
          !telemetryFlagLoading &&
          isAppInsightsStarted()
            ? { 'x-correlation-id': correlationId }
            : {}),
        },
        // signal: ctrl.signal,
      });
      setResponse(res.data);
      return res.data;
    } catch (err: any) {
      console.log(err);
      trackException(err, {
        url,
        method,
        httpStatus: err?.response?.status,
        correlationId,
      });
      toast.error(err.message);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // useEffect cleanup function
    return () => controller && controller.abort();
  }, [controller]);

  return [response, error, loading, axiosFetch] as const;
};
